const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:7071/api";
export const AUTH_SESSION_EXPIRED_EVENT = "elmonet-auth-session-expired";
export const AUTH_PERMISSIONS_CHANGED_EVENT = "elmonet-auth-permissions-changed";

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

export function getAuthToken() {
  return localStorage.getItem("elmonet_auth_token");
}

export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem("elmonet_auth_token", token);
  else localStorage.removeItem("elmonet_auth_token");
}

export function getAuthTokenExpiry(): number | null {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const body = token.split(".", 1)[0].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(body.padEnd(Math.ceil(body.length / 4) * 4, "=")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return 0;
  }
}

function invalidateSession() {
  setAuthToken(null);
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
}

export async function apiClient<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const isFormData = init?.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(!isFormData ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Tuntematon virhe" }));
    if (response.status === 401 && path !== "/auth/login") invalidateSession();
    if (response.status === 403) window.dispatchEvent(new CustomEvent(AUTH_PERMISSIONS_CHANGED_EVENT));
    const details = Array.isArray(body.details)
      ? body.details.map((item: { msg?: string }) => item.msg).filter(Boolean).join(" ")
      : typeof body.details === "string"
        ? body.details
        : "";
    throw new ApiError(
      [body.error || "API-kutsu epäonnistui", details].filter(Boolean).join(": "),
      response.status
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function apiDownload(path: string, init?: RequestInit): Promise<{ blob: Blob; filename: string }> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Tuntematon virhe" }));
    if (response.status === 401) invalidateSession();
    if (response.status === 403) window.dispatchEvent(new CustomEvent(AUTH_PERMISSIONS_CHANGED_EVENT));
    throw new ApiError(body.error || "Tiedoston muodostaminen epäonnistui", response.status);
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || "laskudatan-kasittely.xlsx";
  return { blob: await response.blob(), filename };
}
